import {Video} from "../hooks/useGalleryPhotos";
import {ActionIcon, Badge, Box, Card, Group, Modal, ScrollArea, Text, Tooltip, Loader} from "@mantine/core";
import {IconCheck, IconKey, IconLock, IconTrash} from "@tabler/icons-react";
import {useMutation, useQueryClient} from "@tanstack/react-query";
import {useDisclosure} from "@mantine/hooks";
import axios from "axios";
import config from "../../../../config";
import useAuth from "../../../../utils/auth";
import React, {useState} from "react";
import move from "./Move/move.module.css";
import cx from "clsx";

interface Props {
  galleryId: number | null
  slug: string
  videos: Video[]
  selectMode?: boolean
  selectedVideos?: Set<number>
  onToggleVideo?: (id: number) => void
}

interface CardProps {
  video: Video
  galleryId: number | null
  slug: string
  selectMode?: boolean
  selected?: boolean
  onToggle?: (id: number) => void
}

const VideoCard = ({video, galleryId, slug, selectMode, selected, onToggle}: CardProps) => {
  const auth = useAuth()
  const queryClient = useQueryClient()
  const [opened, {open, close}] = useDisclosure(false)

  const src = `${config.apiURL}/gallery-videos/${slug}/${video.path}`

  const deleteMutation = useMutation({
    mutationFn: () =>
      axios.delete(`${config.apiURL}/api/videos/${video.id}`, {
        headers: {Authorization: `Bearer ${auth.apiKey}`}
      }),
    onSuccess: () => {
      close()
      const data: any = queryClient.getQueryData(['gallery', galleryId])
      if (data) {
        const copy = JSON.parse(JSON.stringify(data))
        copy.videos = (copy.videos ?? []).filter((v: Video) => v.id !== video.id)
        queryClient.setQueryData(['gallery', galleryId], copy)
      }
    }
  })

  const lockMutation = useMutation({
    mutationFn: (lock: boolean) =>
      axios.put(`${config.apiURL}/api/videos/${video.id}/${lock ? "lock" : "unlock"}`, null, {
        headers: {Authorization: `Bearer ${auth.apiKey}`}
      }),
    onSuccess: (_, lock) => {
      const data: any = queryClient.getQueryData(['gallery', galleryId])
      if (data) {
        const copy = JSON.parse(JSON.stringify(data))
        const item = (copy.videos ?? []).find((v: Video) => v.id === video.id)
        if (item) item.locked = lock
        queryClient.setQueryData(['gallery', galleryId], copy)
      }
    }
  })

  if (selectMode) {
    return (
      <Card withBorder p={6} className={move.selectable} style={{flex: "0 0 auto", width: 260}}
            onClick={() => onToggle?.(video.id)}>
        <div className={cx(move.selectableBox, selected && move.selected)}>
          <Box bg="#000" style={{borderRadius: 4, overflow: "hidden"}}>
            <video
              src={src}
              preload="metadata"
              playsInline
              muted
              style={{width: "100%", height: 150, backgroundColor: "#000", display: "block", pointerEvents: "none"}}
            />
          </Box>
          <div className={cx(move.check, !selected && move.checkEmpty)}>
            {selected ? <IconCheck size={16}/> : null}
          </div>
        </div>
        <Text size="xs" truncate mt={6}>{video.name || `Video ${video.id}`}</Text>
      </Card>
    )
  }

  return (
    <>
      <Card withBorder p={6} style={{flex: "0 0 auto", width: 260}}>
        <Box bg="#000" style={{borderRadius: 4, overflow: "hidden"}}>
          <video
            src={src}
            controls
            preload="metadata"
            playsInline
            style={{width: "100%", height: 150, backgroundColor: "#000", display: "block"}}
          />
        </Box>
        <Group justify="space-between" mt={6} gap={4} wrap="nowrap">
          <Tooltip label={video.name} disabled={!video.name}>
            <Text size="xs" truncate style={{flex: 1, minWidth: 0}}>{video.name || `Video ${video.id}`}</Text>
          </Tooltip>
          <Group gap={4} wrap="nowrap">
            {video.locked ? (
              <Tooltip label="Schované">
                <ActionIcon variant="light" color="red" size="sm" onClick={() => lockMutation.mutate(false)}>
                  {lockMutation.isPending ? <Loader size="xs" color="red"/> : <IconLock size={16}/>}
                </ActionIcon>
              </Tooltip>
            ) : (
              <Tooltip label="Veřejné">
                <ActionIcon variant="light" color="green" size="sm" onClick={() => lockMutation.mutate(true)}>
                  {lockMutation.isPending ? <Loader size="xs" color="green"/> : <IconKey size={16}/>}
                </ActionIcon>
              </Tooltip>
            )}
            <Tooltip label="Smazat">
              <ActionIcon variant="filled" color="red" size="sm" onClick={open}>
                <IconTrash size={16}/>
              </ActionIcon>
            </Tooltip>
          </Group>
        </Group>
      </Card>

      <Modal opened={opened} onClose={close} title="Smazat video?">
        <video src={src} controls preload="metadata" playsInline style={{width: "100%", maxHeight: 300, backgroundColor: "#000"}}/>
        <Group justify="right" gap={5} mt="md">
          {deleteMutation.isPending ? (
            <Loader color="red" size="xs"/>
          ) : (
            <>
              <ActionIcon variant="filled" color="red" size="lg" onClick={() => deleteMutation.mutate()}>
                <IconTrash size={18}/>
              </ActionIcon>
            </>
          )}
        </Group>
      </Modal>
    </>
  )
}

const VideoRow = ({videos, galleryId, slug, selectMode, selectedVideos, onToggleVideo}: Props) => {
  if (!videos || videos.length === 0) return null

  return (
    <Box mt="md">
      <Group gap={6} mb={6}>
        <Text fw={600}>Videa</Text>
        <Badge color="grape" variant="light">{videos.length}</Badge>
      </Group>
      <ScrollArea>
        <Group gap={10} wrap="nowrap" pb={8}>
          {videos.map(video => (
            <VideoCard key={video.id} video={video} galleryId={galleryId} slug={slug}
                       selectMode={selectMode}
                       selected={!!selectedVideos?.has(video.id)}
                       onToggle={onToggleVideo}/>
          ))}
        </Group>
      </ScrollArea>
    </Box>
  )
}

export default VideoRow
